import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalType = 'privacy' | 'terms' | null;
interface LegalModalProps {
  type: LegalType;
  onClose: () => void;
}
const content = {
  privacy: {
    title: 'Política de Privacidad',
    body: [{
      heading: '1. Información que recopilamos',
      text: 'Al realizar una compra, recopilamos únicamente la información necesaria para procesar tu pedido: nombre, dirección de correo electrónico y datos de pago (procesados de forma segura por nuestro proveedor de pagos). No almacenamos información de tarjetas de crédito.'
    }, {
      heading: '2. Uso de la información',
      text: 'Utilizamos tu información exclusivamente para: (a) entregar el producto digital que has adquirido, (b) enviarte confirmaciones de compra, y (c) brindarte soporte si lo necesitas. No vendemos, alquilamos ni compartimos tu información personal con terceros.'
    }, {
      heading: '3. Seguridad',
      text: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información. Todas las transacciones se realizan a través de conexiones cifradas (SSL/TLS).'
    }, {
      heading: '4. Cookies',
      text: 'Este sitio puede utilizar cookies técnicas necesarias para el funcionamiento básico de la página. No utilizamos cookies de rastreo publicitario de terceros.'
    }, {
      heading: '5. Tus derechos',
      text: 'Tienes derecho a solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los medios disponibles en esta página.'
    }, {
      heading: '6. Cambios a esta política',
      text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta misma página con la fecha de actualización correspondiente.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    body: [{
      heading: '1. Aceptación de los términos',
      text: 'Al adquirir y/o descargar "3 Bases, 15 Platos", aceptas los presentes términos y condiciones en su totalidad. Si no estás de acuerdo con alguno de estos términos, te pedimos que no realices la compra.'
    }, {
      heading: '2. Descripción del producto',
      text: '"3 Bases, 15 Platos" es una guía digital de meal prep diseñada para mujeres latinas en USA. El producto se entrega en formato digital (PDF) de forma inmediata tras la confirmación del pago. No se envían productos físicos.'
    }, {
      heading: '3. Licencia de uso',
      text: 'Al adquirir este producto, obtienes una licencia personal, no exclusiva e intransferible para uso propio. Queda estrictamente prohibida la reproducción, distribución, reventa o compartición del contenido sin autorización expresa por escrito.'
    }, {
      heading: '4. Política de reembolsos',
      text: 'Dado que se trata de un producto digital de entrega inmediata, no ofrecemos reembolsos una vez que el archivo ha sido descargado. Si tienes problemas técnicos para acceder al producto, contáctanos y lo resolveremos a la brevedad.'
    }, {
      heading: '5. Resultados',
      text: 'Los resultados descritos en esta página son ejemplos reales pero no garantizados. Los resultados individuales pueden variar dependiendo del esfuerzo, la constancia y las circunstancias personales de cada usuaria.'
    }, {
      heading: '6. Limitación de responsabilidad',
      text: 'Este producto es de carácter informativo y educativo. No constituye asesoramiento nutricional, médico ni dietético profesional. Consulta a un profesional de la salud antes de realizar cambios significativos en tu dieta.'
    }, {
      heading: '7. Ley aplicable',
      text: 'Estos términos se rigen por las leyes aplicables en el lugar de residencia del vendedor. Cualquier disputa será resuelta de buena fe entre las partes.'
    }]
  }
};
export function LegalModal({
  type,
  onClose
}: LegalModalProps) {
  if (!type) return null;
  const data = content[type];
  return <AnimatePresence data-id="element-57">
      {type && <>
          {/* Backdrop */}
          <motion.div key="backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-espresso/60 backdrop-blur-sm z-50" data-id="element-58" />

          {/* Modal */}
          <motion.div key="modal" initial={{
        opacity: 0,
        y: 60,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="fixed inset-x-4 top-[5%] bottom-[5%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl z-50 flex flex-col" data-id="element-59">
            <div className="bg-warm-white rounded-2xl shadow-2xl flex flex-col h-full overflow-hidden border border-stone-200" data-id="element-60">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-stone-200 bg-cream shrink-0" data-id="element-61">
                <h2 className="text-xl font-bold text-espresso font-serif" data-id="element-62">
                  {data.title}
                </h2>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-200 transition-colors text-espresso/60 hover:text-espresso" aria-label="Cerrar" data-id="element-63">
                  <X className="w-5 h-5" data-id="element-64" />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6" data-id="element-65">
                {data.body.map((section, i) => <div key={i} data-id="element-66">
                    <h3 className="font-bold text-espresso mb-2 font-serif text-base" data-id="element-67">
                      {section.heading}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-68">
                      {section.text}
                    </p>
                  </div>)}
                <p className="text-xs text-gray-400 pt-4 border-t border-stone-100" data-id="element-69">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}